import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const NODES = [
  { tag: "STEP 01", label: "INPUT", sub: "canonicalize + hash", hash: "features → a4e1…09dd", c: "#0A94A5" },
  { tag: "STEP 02", label: "MODEL · risk_model v3.2", sub: "fingerprint verified · APPROVED", hash: "3f9a…c17b", c: "#0A94A5" },
  { tag: "STEP 03", label: "OUTPUT · decision", sub: "APPROVE · confidence 0.91", hash: "→ 7c2b…", c: "#0A94A5" },
  { tag: "STEP 04", label: "SIGN · HSM", sub: "Ed25519 · hardware-held key", hash: "sig 2m0s…", c: "#11A050" },
  { tag: "STEP 05", label: "VAULT · append-only", sub: "chain-hashed · tamper-evident", hash: "…7c2 → 9be → f01", c: "#11A050" },
];

const BOX_X = 26, BOX_W = 300, H = 74, GAP = 30, TOP = 18;
const cx = BOX_X + BOX_W / 2;
const nodeY = (i) => TOP + i * (H + GAP);

export const BlueprintChain = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [30, -40]);
  const vaultBottom = nodeY(NODES.length - 1) + H;
  const total = vaultBottom + GAP + 52;

  return (
    <motion.div ref={ref} style={{ y }}
      initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.9, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}>
      <div className="bp" data-testid="hero-blueprint">
        <div className="cap"><span>schematic · decision.attest()</span><span className="live">verify() authentic</span></div>
        <svg viewBox={`0 0 ${BOX_X * 2 + BOX_W + 80} ${total}`} role="img"
          aria-label="Blueprint of the VeriAuth attestation flow: input, model, output, sign, append-only vault, independently verifiable">
          <defs>
            <linearGradient id="bpgrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#0BA3AE" /><stop offset="1" stopColor="#11A050" />
            </linearGradient>
            <marker id="arrow" markerWidth="8" markerHeight="8" refX="4" refY="4" orient="auto">
              <path d="M1 1 L6 4 L1 7" fill="none" stroke="#0A94A5" strokeWidth="1.3" />
            </marker>
            <filter id="bpglow" x="-120%" y="-120%" width="340%" height="340%">
              <feGaussianBlur stdDeviation="3.4" result="b" />
              <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* connectors — intro draw + continuous flow overlay */}
          {NODES.slice(0, -1).map((_, i) => {
            const y1 = nodeY(i) + H, y2 = nodeY(i + 1);
            return (
              <g key={i}>
                <motion.line x1={cx} y1={y1} x2={cx} y2={y2} stroke="#B4C8C1"
                  strokeWidth="1.5" markerEnd="url(#arrow)"
                  initial={{ pathLength: 0, opacity: 0 }} animate={{ pathLength: 1, opacity: 1 }}
                  transition={{ duration: 0.4, delay: 0.7 + i * 0.28 }} />
                <line className="bp-flow" x1={cx} y1={y1} x2={cx} y2={y2} />
              </g>
            );
          })}
          {/* connector to verifier */}
          <motion.line x1={cx} y1={vaultBottom} x2={cx} y2={vaultBottom + GAP} stroke="url(#bpgrad)"
            strokeWidth="1.6" markerEnd="url(#arrow)"
            initial={{ pathLength: 0 }} animate={{ pathLength: 1 }} transition={{ duration: 0.4, delay: 0.7 + NODES.length * 0.28 }} />
          <line className="bp-flow" x1={cx} y1={vaultBottom} x2={cx} y2={vaultBottom + GAP} />

          {/* nodes */}
          {NODES.map((n, i) => (
            <motion.g key={n.label}
              initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.55 + i * 0.28, ease: [0.22, 1, 0.36, 1] }}>
              <rect x={BOX_X} y={nodeY(i)} width={BOX_W} height={H} rx="12"
                fill="#FFFFFF" stroke={n.c} strokeOpacity="0.5" strokeWidth="1.2" />
              <rect className="bp-accent" style={{ animationDelay: `${i * 0.5}s` }} x={BOX_X} y={nodeY(i)} width="4" height={H} rx="2" fill={n.c} />
              <text className="ntag" x={BOX_X + 18} y={nodeY(i) + 20} fill={n.c}>{n.tag}</text>
              <text className="nlabel" x={BOX_X + 18} y={nodeY(i) + 38}>{n.label}</text>
              <text className="nsub" x={BOX_X + 18} y={nodeY(i) + 54}>{n.sub}</text>
              <text className="hash" x={BOX_X + 18} y={nodeY(i) + 67}>{n.hash}</text>
            </motion.g>
          ))}

          {/* traveling packet — pulses down the chain continuously */}
          <motion.circle cx={cx} cy={nodeY(0) + H / 2} r="5" fill="#0A94A5" filter="url(#bpglow)"
            initial={{ opacity: 0 }}
            animate={{
              cy: [...NODES.map((_, i) => nodeY(i) + H / 2), vaultBottom + GAP + 22],
              opacity: [0, 1, 1, 1, 1, 1],
            }}
            transition={{ duration: 3.4, delay: 1.8, repeat: Infinity, repeatDelay: 0.5, ease: "easeInOut", times: [0, 0.2, 0.4, 0.6, 0.8, 1] }} />

          {/* verifier pill */}
          <motion.g initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.7 + NODES.length * 0.28 + 0.2 }}>
            <rect x={cx - 130} y={vaultBottom + GAP} width="260" height="44" rx="22"
              fill="#E3F7EB" stroke="url(#bpgrad)" strokeWidth="1.4" />
            <circle cx={cx - 100} cy={vaultBottom + GAP + 22} r="10" fill="none" stroke="#11A050" strokeWidth="1.6" />
            <path d={`M ${cx - 105} ${vaultBottom + GAP + 22} l 3.5 4 l 7 -8`} fill="none" stroke="#11A050" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <text x={cx - 78} y={vaultBottom + GAP + 20} fill="#0A1310" fontSize="11.5" fontWeight="700" fontFamily="var(--mono)">Independent verifier</text>
            <text x={cx - 78} y={vaultBottom + GAP + 34} fill="#3E4D48" fontSize="9" fontFamily="var(--mono)">no operator trust required</text>
          </motion.g>
        </svg>
      </div>
    </motion.div>
  );
};
